import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronDown,
  Copy,
  Download,
  Eye,
  FilePlus2,
  Pencil,
  Plus,
  Save,
  Send,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { applyWorksheetStyle, styleWorkbook } from "../excel";
import {
  Button,
  CatalogLabel,
  ConfirmDialog,
  EmptyState,
  FormField,
  Modal,
  NumericInput,
  Pagination,
  Panel,
  SearchBox,
  StatusBadge,
} from "../components/ui";
import { formatNumber } from "../numbers";
import { PageHeader } from "../components/shell";
import { ImportsPage } from "./imports";
import type {
  BomDetail,
  BomLine,
  BomListItem,
  Catalog,
  CatalogType,
  PageMeta,
} from "../types";
import { formatDate } from "./catalogs";

type BomResponse = { items: BomListItem[]; meta: PageMeta };
type CatalogResponse = { items: Catalog[]; meta: PageMeta };
type CatalogSet = Record<
  "product" | "customer" | "material" | "unit" | "warehouse",
  Catalog[]
>;
type CustomField = {
  id: string;
  applies_to: "bom_header" | "bom_line";
  field_key: string;
  label: string;
  field_type: "text" | "number" | "date" | "boolean" | "select";
  selectOptions: string[];
  is_required: number;
  is_active: number;
};

function useCatalogs(types: Array<keyof CatalogSet>) {
  const result = useQuery({
    queryKey: ["bom-form-catalogs", types.join(",")],
    queryFn: async () => {
      const entries = await Promise.all(
        types.map(
          async (type) =>
            [
              type,
              await api<CatalogResponse>(
                `/catalogs/${type}?page=1&pageSize=100&active=true`,
              ),
            ] as const,
        ),
      );
      return Object.fromEntries(
        entries.map(([type, value]) => [type, value.items]),
      ) as CatalogSet;
    },
  });
  return result.data;
}

function useCustomFields(appliesTo: CustomField["applies_to"]) {
  const result = useQuery({
    queryKey: ["custom-fields", appliesTo],
    queryFn: () => api<{ items: CustomField[] }>(`/custom-fields?appliesTo=${appliesTo}`),
  });
  return result.data?.items.filter((field) => field.is_active === 1) ?? [];
}

function useChildBomVersions(excludeBomId: string, enabled: boolean) {
  const result = useQuery({
    queryKey: ["child-bom-versions", excludeBomId],
    enabled,
    queryFn: async () => {
      const firstPage = await api<BomResponse>("/boms?page=1&pageSize=100");
      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(0, firstPage.meta.totalPages - 1) }, (_, index) =>
          api<BomResponse>(`/boms?page=${index + 2}&pageSize=100`),
        ),
      );
      return [firstPage, ...remainingPages]
        .flatMap((response) => response.items)
        .filter((bom) => bom.id !== excludeBomId);
    },
  });
  return result.data ?? [];
}

function CustomFieldInputs({
  fields,
  values,
  onChange,
}: {
  fields: CustomField[];
  values: Record<string, unknown>;
  onChange(values: Record<string, unknown>): void;
}) {
  if (!fields.length) return null;
  return (
    <fieldset className="custom-fields-form">
      <legend>Thông tin bổ sung</legend>
      <div className="form-grid">
        {fields.map((field) => {
          const value = values[field.field_key];
          const setValue = (next: unknown) => onChange({ ...values, [field.field_key]: next });
          return (
            <FormField key={field.id} label={field.label}>
              {field.field_type === "select" ? (
                <select
                  required={field.is_required === 1}
                  value={String(value ?? "")}
                  onChange={(event) => setValue(event.target.value)}
                >
                  <option value="">Chọn {field.label.toLowerCase()}</option>
                  {field.selectOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : field.field_type === "boolean" ? (
                <select
                  required={field.is_required === 1}
                  value={value === true ? "true" : value === false ? "false" : ""}
                  onChange={(event) => setValue(event.target.value === "" ? "" : event.target.value === "true")}
                >
                  <option value="">Chưa chọn</option>
                  <option value="true">Có</option>
                  <option value="false">Không</option>
                </select>
              ) : (
                field.field_type === "number" ? (
                  <NumericInput required={field.is_required === 1} value={String(value ?? "")} onValueChange={setValue} />
                ) : (
                  <input
                    required={field.is_required === 1}
                    type={field.field_type === "date" ? "date" : "text"}
                    value={String(value ?? "")}
                    onChange={(event) => setValue(event.target.value)}
                  />
                )
              )}
            </FormField>
          );
        })}
      </div>
    </fieldset>
  );
}

function CustomFieldValues({
  fields,
  values,
}: {
  fields: CustomField[];
  values: Record<string, unknown>;
}) {
  const visible = fields.filter((field) => values[field.field_key] !== undefined && values[field.field_key] !== "");
  if (!visible.length) return null;
  return (
    <dl className="custom-field-values">
      {visible.map((field) => (
        <div key={field.id}>
          <dt>{field.label}</dt>
          <dd>{values[field.field_key] === true ? "Có" : values[field.field_key] === false ? "Không" : field.field_type === "number" ? formatNumber(values[field.field_key]) : String(values[field.field_key])}</dd>
        </div>
      ))}
    </dl>
  );
}

export function BomsPage({ roles }: { roles: string[] }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<"list" | "transfer">("list");
  const [create, setCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => setPage(1), [search, status]);
  const boms = useQuery({
    queryKey: ["boms", search, status, page],
    queryFn: () =>
      api<BomResponse>(
        `/boms?page=${page}&pageSize=25&q=${encodeURIComponent(search)}${status ? `&status=${status}` : ""}`,
      ),
  });
  const selection = useQuery({
    queryKey: ["bom", selectedId],
    queryFn: () => api<BomDetail>(`/boms/${selectedId}`),
    enabled: Boolean(selectedId),
  });
  const client = useQueryClient();
  function refresh() {
    client.invalidateQueries({ queryKey: ["boms"] });
    if (selectedId) client.invalidateQueries({ queryKey: ["bom", selectedId] });
  }
  return (
    <div className="page bom-page">
      <PageHeader
        title="Quản lý BOM & Định mức"
        actions={
          <>
            <Button variant="outline" onClick={() => setTab("transfer")}>
              <FilePlus2 size={17} /> Nhập BOM từ Excel
            </Button>
            <Button onClick={() => setCreate(true)}>
              <Plus size={17} /> Tạo BOM
            </Button>
          </>
        }
      />
      <div className="section-tabs" role="tablist" aria-label="Quản lý BOM">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "list"}
          className={tab === "list" ? "selected" : ""}
          onClick={() => setTab("list")}
        >
          Danh sách BOM
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "transfer"}
          className={tab === "transfer" ? "selected" : ""}
          onClick={() => setTab("transfer")}
        >
          Nhập BOM từ Excel
        </button>
      </div>
      {tab === "list" ? (
        <>
      <Panel className="bom-list-panel">
        <div className="table-toolbar bom-toolbar">
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Tìm theo mã BOM, sản phẩm, khách hàng..."
          />
          <label className="filter-select">
            <span>Trạng thái:</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Tất cả</option>
              <option value="draft">Bản nháp</option>
              <option value="in_review">Chờ duyệt</option>
              <option value="active">Đang áp dụng</option>
              <option value="superseded">Hết hiệu lực</option>
            </select>
            <ChevronDown size={15} />
          </label>
        </div>
        {boms.isLoading ? (
          <div className="loading-space">Đang tải BOM…</div>
        ) : !boms.data?.items.length ? (
          <EmptyState
            title="Chưa có BOM nào"
            detail="Tạo BOM thủ công hoặc import XLSX để bắt đầu quản lý định mức."
            action={
              <Button onClick={() => setCreate(true)}>
                <FilePlus2 size={17} /> Tạo BOM
              </Button>
            }
          />
        ) : (
          <>
            <div className="table-scroll">
              <table className="bom-table">
                <thead>
                  <tr>
                    <th>Mã BOM</th>
                    <th>Tên BOM</th>
                    <th>Sản phẩm</th>
                    <th>Khách hàng</th>
                    <th>Phiên bản</th>
                    <th>Trạng thái</th>
                    <th>Cập nhật</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {boms.data.items.map((bom) => (
                    <tr
                      key={bom.id}
                      className={selectedId === bom.id ? "selected-row" : ""}
                    >
                      <td className="code-cell">{bom.code}</td>
                      <td>{bom.name || "—"}</td>
                      <td>{bom.product.name}</td>
                      <td>{bom.customer.name}</td>
                      <td>v{bom.versionNo}</td>
                      <td>
                        <StatusBadge status={bom.status} />
                      </td>
                      <td>{formatDate(bom.updatedAt)}</td>
                      <td className="row-actions">
                        <button
                          onClick={() => setSelectedId(bom.id)}
                          title="Xem"
                        >
                          <Eye size={17} />
                        </button>
                        <button
                          onClick={() => setSelectedId(bom.id)}
                          title="Chỉnh sửa"
                        >
                          <Pencil size={17} />
                        </button>
                        <button
                          title="Sao chép"
                          onClick={() =>
                            navigator.clipboard?.writeText(bom.code)
                          }
                        >
                          <Copy size={17} />
                        </button>
                        {roles.includes("superadmin") && <DeleteBomButton bom={bom} onDeleted={refresh} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={boms.data.meta} onPage={setPage} />
          </>
        )}
      </Panel>
      {selectedId && (
        <Modal
          open
          title="Chi tiết BOM"
          onClose={() => setSelectedId(null)}
          hideHeader
        >
          <BomDetailPanel
            bom={selection.data}
            loading={selection.isLoading}
            roles={roles}
            onClose={() => setSelectedId(null)}
            onRefresh={refresh}
          />
        </Modal>
      )}
        </>
      ) : (
        <ImportsPage
          embedded
          onCompleted={(bomIds) => {
            setTab("list");
            setPage(1);
            setSelectedId(bomIds[0] ?? null);
            client.invalidateQueries({ queryKey: ["boms"] });
          }}
        />
      )}
      <CreateBomModal
        open={create}
        onClose={() => setCreate(false)}
        onCreated={(id) => {
          setCreate(false);
          setSelectedId(id);
          refresh();
        }}
      />
    </div>
  );
}

function DeleteBomButton({
  bom,
  onDeleted,
}: {
  bom: BomListItem;
  onDeleted(): void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api(`/boms/${bom.id}`, { method: "DELETE" });
      setOpen(false);
      onDeleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể xóa BOM.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button type="button" title="Xóa BOM" aria-label={`Xóa ${bom.code}`} onClick={() => setOpen(true)}>
        <Trash2 size={17} />
      </button>
      <ConfirmDialog
        open={open}
        title={error ? "Không thể xóa BOM" : "Xóa BOM"}
        message={error || `Bạn có chắc muốn xóa BOM “${bom.code}” không? Toàn bộ dòng vật tư và lịch sử của BOM này sẽ bị xóa, không thể hoàn tác.`}
        confirmLabel="Xóa BOM"
        pending={busy}
        onClose={() => setOpen(false)}
        onConfirm={() => void remove()}
      />
    </>
  );
}

function CreateBomModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose(): void;
  onCreated(id: string): void;
}) {
  const catalogs = useCatalogs(["product", "customer"]);
  const fields = useCustomFields("bom_header");
  const [productId, setProductId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setProductId("");
      setCustomerId("");
      setName("");
      setNote("");
      setCustomFields({});
      setError("");
    }
  }, [open]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = await api<{ id: string }>("/boms", {
        method: "POST",
        body: JSON.stringify({
          productId,
          customerId,
          name,
          note,
          customFields,
        }),
      });
      onCreated(result.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể tạo BOM.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} title="Tạo BOM nháp" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <p className="muted">
          Chọn sản phẩm và khách hàng để lập BOM nháp. Khi cần thêm thông tin riêng, hãy tạo trường tùy chọn trong Quản trị hệ thống.
        </p>
        <CatalogSelect
          label="Sản phẩm"
          value={productId}
          onChange={setProductId}
          items={catalogs?.product ?? []}
          required
        />
        <CatalogSelect
          label="Khách hàng"
          value={customerId}
          onChange={setCustomerId}
          items={catalogs?.customer ?? []}
          required
        />
        <FormField label="Tên BOM (tùy chọn)">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={240}
            placeholder="Ví dụ: Định mức bột cá tra xuất khẩu"
          />
        </FormField>
        <FormField label="Ghi chú">
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={2000}
          />
        </FormField>
        <CustomFieldInputs
          fields={fields}
          values={customFields}
          onChange={setCustomFields}
        />
        {!catalogs && (
          <p className="muted">Đang tải danh mục sản phẩm và khách hàng…</p>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button disabled={saving || !catalogs} type="submit">
            Tạo bản nháp
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function BomDetailPanel({
  bom,
  loading,
  roles,
  onClose,
  onRefresh,
}: {
  bom?: BomDetail;
  loading: boolean;
  roles: string[];
  onClose(): void;
  onRefresh(): void;
}) {
  const [addLine, setAddLine] = useState(false);
  const [editHeader, setEditHeader] = useState(false);
  const [approval, setApproval] = useState(false);
  const [message, setMessage] = useState("");
  const headerFields = useCustomFields("bom_header");
  const lineFields = useCustomFields("bom_line");
  const canApprove =
    roles.includes("superadmin") || roles.includes("bom_approver");
  async function submit() {
    if (!bom) return;
    setMessage("");
    try {
      await api(`/boms/${bom.id}/submit`, { method: "POST" });
      setMessage("Đã gửi BOM sang bước phê duyệt.");
      onRefresh();
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "Không thể gửi phê duyệt.",
      );
    }
  }
  async function approve(reason: string) {
    if (!bom) return;
    setMessage("");
    try {
      await api(`/boms/${bom.id}/approve`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      setMessage("Đã phê duyệt BOM.");
      onRefresh();
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "Không thể phê duyệt.",
      );
    }
  }
  return (
    <Panel
      className="bom-detail"
      title={
        loading ? (
          "Đang tải chi tiết…"
        ) : (
          <>
            Chi tiết định mức vật tư:{" "}
            <span className="detail-code">{bom?.code}</span>{" "}
            <small>({bom?.product.name})</small>
          </>
        )
      }
      actions={
        <>
          <Button
            variant="outline"
            disabled={!bom || bom.status !== "draft"}
            onClick={() => setEditHeader(true)}
          >
            <Pencil size={16} /> Sửa thông tin
          </Button>
          {bom && <ExportBomDetailButton bom={bom} headerFields={headerFields} lineFields={lineFields} />}
          <Button
            variant="success"
            disabled={!bom || bom.status !== "draft"}
            onClick={() => setAddLine(true)}
          >
            <Plus size={16} /> Thêm vật tư
          </Button>
          {bom?.status === "draft" && (
            <Button variant="outline" onClick={submit}>
              <Send size={16} /> Gửi duyệt
            </Button>
          )}
          {bom?.status === "in_review" && canApprove && (
            <Button onClick={() => setApproval(true)}>
              <CheckCircle2 size={16} /> Phê duyệt
            </Button>
          )}
          <button className="icon-action" onClick={onClose} title="Đóng">
            ×
          </button>
        </>
      }
    >
      {message && <p className="form-message inline-message">{message}</p>}
      {bom && (
        <dl className="bom-header-summary">
          <div><dt>Tên BOM</dt><dd>{bom.name || bom.code}</dd></div>
          <div><dt>Sản phẩm</dt><dd>{bom.product.name}</dd></div>
          <div><dt>Khách hàng</dt><dd>{bom.customer.name}</dd></div>
          {bom.note && <div className="bom-header-note"><dt>Ghi chú</dt><dd>{bom.note}</dd></div>}
        </dl>
      )}
      {bom && <CustomFieldValues fields={headerFields} values={bom.customFields} />}
      {!bom ? (
        <div className="loading-space">Đang tải chi tiết BOM…</div>
      ) : !bom.lines.length ? (
        <EmptyState
          title="BOM chưa có vật tư"
          detail="Thêm dòng vật tư để hoàn thành bản nháp định mức."
          action={
            bom.status === "draft" ? (
              <Button variant="success" onClick={() => setAddLine(true)}>
                Thêm vật tư
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="table-scroll">
            <table className="line-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vật tư</th>
                  <th>BOM con</th>
                  <th>ĐVT</th>
                  <th>Định mức sử dụng</th>
                  <th>Mã kế toán</th>
                  <th>KL thành phẩm</th>
                  <th>KL vật tư sử dụng</th>
                  <th>Hao hụt (%)</th>
                  <th>Ghi chú</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bom.lines.map((line) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    bom={bom}
                    fields={lineFields}
                    onRefresh={onRefresh}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="detail-footer">
            {bom.lines.length} vật tư trong phiên bản v{bom.versionNo}
          </div>
        </>
      )}
      <AddLineModal
        open={addLine}
        bomId={bom?.id ?? ""}
        onClose={() => setAddLine(false)}
        onSaved={() => {
          setAddLine(false);
          onRefresh();
        }}
      />
      {bom && (
        <EditBomHeaderModal
          open={editHeader}
          bom={bom}
          onClose={() => setEditHeader(false)}
          onSaved={() => {
            setEditHeader(false);
            onRefresh();
          }}
        />
      )}
      <ApproveBomModal
        open={approval}
        needsReason={roles.includes("superadmin")}
        onClose={() => setApproval(false)}
        onApprove={async (reason) => {
          await approve(reason);
          setApproval(false);
        }}
      />
    </Panel>
  );
}

function ApproveBomModal({
  open,
  needsReason,
  onClose,
  onApprove,
}: {
  open: boolean;
  needsReason: boolean;
  onClose(): void;
  onApprove(reason: string): Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);
  async function submitApproval(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onApprove(reason);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể phê duyệt BOM.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} title="Phê duyệt BOM" onClose={onClose}>
      <form className="modal-form" onSubmit={submitApproval}>
        <p className="muted">BOM được áp dụng ngay sau khi phê duyệt. Nếu cần thêm thông tin riêng, hãy tạo trường tùy chọn trong Quản trị.</p>
        {needsReason && (
          <FormField label="Lý do phê duyệt (chỉ cần khi bạn là người tạo BOM)">
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={1000}
            />
          </FormField>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            <CheckCircle2 size={16} /> Phê duyệt
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditBomHeaderModal({
  open,
  bom,
  onClose,
  onSaved,
}: {
  open: boolean;
  bom: BomDetail;
  onClose(): void;
  onSaved(): void;
}) {
  const catalogs = useCatalogs(["product", "customer"]);
  const fields = useCustomFields("bom_header");
  const [code, setCode] = useState(bom.code);
  const [name, setName] = useState(bom.name);
  const [productId, setProductId] = useState(bom.product.id);
  const [customerId, setCustomerId] = useState(bom.customer.id);
  const [note, setNote] = useState(bom.note);
  const [customFields, setCustomFields] = useState<Record<string, unknown>>(bom.customFields);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(bom.code);
    setName(bom.name);
    setProductId(bom.product.id);
    setCustomerId(bom.customer.id);
    setNote(bom.note);
    setCustomFields(bom.customFields ?? {});
    setError("");
  }, [bom, open]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/boms/${bom.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          code,
          name,
          productId,
          customerId,
          note,
          customFields,
        }),
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể cập nhật BOM.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="Sửa thông tin BOM" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <FormField label="Mã BOM">
          <input required value={code} onChange={(event) => setCode(event.target.value)} maxLength={80} />
        </FormField>
        <FormField label="Tên BOM">
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={240} />
        </FormField>
        <CatalogSelect label="Sản phẩm" value={productId} onChange={setProductId} items={catalogs?.product ?? []} required />
        <CatalogSelect label="Khách hàng" value={customerId} onChange={setCustomerId} items={catalogs?.customer ?? []} required />
        <FormField label="Ghi chú">
          <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} />
        </FormField>
        <CustomFieldInputs fields={fields} values={customFields} onChange={setCustomFields} />
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
          <Button type="submit" disabled={saving || !catalogs}><Save size={16} /> Lưu thay đổi</Button>
        </div>
      </form>
    </Modal>
  );
}

function exportCellValue(value: unknown): string | number | boolean {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  return JSON.stringify(value);
}

function customFieldLabel(key: string, fields: CustomField[]) {
  return fields.find((field) => field.field_key === key)?.label ?? key;
}

function ExportBomDetailButton({
  bom,
  headerFields,
  lineFields,
}: {
  bom: BomDetail;
  headerFields: CustomField[];
  lineFields: CustomField[];
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function exportFile() {
    setBusy(true);
    setError("");
    try {
      const XLSX = await import("xlsx-js-style");
      const headerExtraKeys = Object.keys(bom.customFields);
      const headerRows: Array<Array<string | number | boolean>> = [
        ["Thông tin BOM", "Giá trị"],
        ["Mã BOM", bom.code],
        ["Tên BOM", bom.name || bom.code],
        ["Sản phẩm", bom.product.name],
        ["Khách hàng", bom.customer.name],
        ["Phiên bản", bom.versionNo],
        ["Trạng thái", bom.status],
        ["Ghi chú", bom.note],
        ...headerExtraKeys.map((key) => [`Thông tin bổ sung: ${customFieldLabel(key, headerFields)}`, exportCellValue(bom.customFields[key])]),
      ];
      const lineExtraKeys = Array.from(new Set(bom.lines.flatMap((line) => Object.keys(line.customFields))));
      const materialRows = bom.lines.map((line) => ({
        "STT": line.lineNo,
        "Mã vật tư": line.material.code,
        "Tên vật tư": line.material.name,
        "Đơn vị tính": line.unit.code,
        "Định mức sử dụng": line.quantity,
        "Hao hụt (%)": line.wastagePercent,
        "BOM con": line.childBom ? `${line.childBom.code} · v${line.childBom.versionNo}` : "",
        "Sản phẩm BOM con": line.childBom?.productName ?? "",
        "Khách hàng BOM con": line.childBom?.customerName ?? "",
        "Mã kế toán": exportCellValue(line.customFields.accountCode),
        "Khối lượng thành phẩm": exportCellValue(line.customFields.finishedWeight),
        "Khối lượng vật tư sử dụng": exportCellValue(line.customFields.usedWeight),
        "Ghi chú": line.note,
        ...Object.fromEntries(lineExtraKeys.map((key) => [`Thông tin bổ sung: ${customFieldLabel(key, lineFields)}`, exportCellValue(line.customFields[key])])),
      }));
      const workbook = XLSX.utils.book_new();
      const informationSheet = XLSX.utils.aoa_to_sheet(headerRows);
      const materialSheet = XLSX.utils.json_to_sheet(materialRows);
      applyWorksheetStyle(XLSX, informationSheet, { headerRows: [0], dataStartsAt: 1, columnWidths: [36, 62] });
      applyWorksheetStyle(XLSX, materialSheet, { headerRows: [0], dataStartsAt: 1, numberColumns: [0, 4, 5, 9, 10], columnWidths: [8, 18, 34, 14, 18, 14, 24, 28, 28, 18, 22, 30] });
      XLSX.utils.book_append_sheet(workbook, informationSheet, "Thông tin BOM");
      XLSX.utils.book_append_sheet(workbook, materialSheet, "Dòng vật tư");
      styleWorkbook(XLSX, workbook, `${bom.code} - chi tiết BOM`);
      XLSX.writeFile(workbook, `${bom.code.replace(/[^a-zA-Z0-9._-]/g, "-")}-chi-tiet.xlsx`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể xuất tệp Excel.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="bom-export-action">
      <Button variant="outline" disabled={busy} onClick={() => void exportFile()}>
        <Download size={16} /> Xuất Excel
      </Button>
      {error && <span className="form-error">{error}</span>}
    </span>
  );
}

function LineRow({
  line,
  bom,
  fields,
  onRefresh,
}: {
  line: BomLine;
  bom: BomDetail;
  fields: CustomField[];
  onRefresh(): void;
}) {
  const [removing, setRemoving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  async function remove() {
    setRemoving(true);
    try {
      await api(`/boms/${bom.id}/lines/${line.id}`, { method: "DELETE" });
      onRefresh();
      setConfirming(false);
    } finally {
      setRemoving(false);
    }
  }
  return (
    <tr>
      <td>{line.lineNo}</td>
      <td><CatalogLabel code={line.material.code} name={line.material.name} /></td>
      <td>{line.childBom ? `${line.childBom.code} · v${line.childBom.versionNo}` : "—"}</td>
      <td>{line.unit.name}</td>
      <td className="num-cell">{formatNumber(line.quantity)}</td>
      <td>{String(line.customFields.accountCode ?? "—")}</td>
      <td className="num-cell">{formatNumber(line.customFields.finishedWeight)}</td>
      <td className="num-cell">{formatNumber(line.customFields.usedWeight)}</td>
      <td className="num-cell">{formatNumber(line.wastagePercent)}</td>
      <td>
        {line.note || "—"}
        <CustomFieldValues fields={fields} values={line.customFields} />
      </td>
      <td className="row-actions">
        {bom.status === "draft" && (
          <>
            <button onClick={() => setEditing(true)} title="Sửa dòng">
              <Pencil size={16} />
            </button>
            <button disabled={removing} onClick={() => setConfirming(true)} title="Xóa dòng">
              <Trash2 size={16} />
            </button>
            <EditLineModal
              open={editing}
              bomId={bom.id}
              line={line}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                onRefresh();
              }}
            />
            <ConfirmDialog
              open={confirming}
              title="Xóa vật tư khỏi BOM"
              message={`Bạn có chắc muốn xóa vật tư “${line.material.name}” khỏi BOM này?`}
              confirmLabel="Xóa vật tư"
              pending={removing}
              onClose={() => setConfirming(false)}
              onConfirm={() => void remove()}
            />
          </>
        )}
      </td>
    </tr>
  );
}

function AddLineModal({
  open,
  bomId,
  onClose,
  onSaved,
}: {
  open: boolean;
  bomId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const catalogs = useCatalogs(["material", "unit"]);
  const fields = useCustomFields("bom_line");
  const [materialId, setMaterialId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [childBomVersionId, setChildBomVersionId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [wastagePercent, setWastagePercent] = useState("0");
  const [note, setNote] = useState("");
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const childBoms = useChildBomVersions(bomId, open);
  const selectedMaterial = (catalogs?.material ?? []).find((material) => material.id === materialId);
  const materialUnits = selectedMaterial?.defaultUnitId
    ? (catalogs?.unit ?? []).filter((unit) => unit.id === selectedMaterial.defaultUnitId)
    : [];
  function setProductionField(key: "accountCode" | "finishedWeight" | "usedWeight", value: string) {
    setCustomFields((current) => ({ ...current, [key]: value }));
  }
  useEffect(() => {
    if (open) {
      setMaterialId("");
      setUnitId("");
      setChildBomVersionId("");
      setQuantity("");
      setWastagePercent("0");
      setNote("");
      setCustomFields({});
      setError("");
    }
  }, [open]);
  function selectMaterial(id: string) {
    setMaterialId(id);
    const material = (catalogs?.material ?? []).find((item) => item.id === id);
    setUnitId(material?.defaultUnitId ?? "");
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/boms/${bomId}/lines`, {
        method: "POST",
        body: JSON.stringify({
          materialId,
          unitId,
          quantity,
          wastagePercent,
          childBomVersionId: childBomVersionId || null,
          note,
          customFields,
        }),
      });
      onSaved();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Không thể thêm vật tư.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} title="Thêm vật tư vào BOM" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <CatalogSelect
          label="Vật tư"
          items={catalogs?.material ?? []}
          value={materialId}
          onChange={selectMaterial}
          required
        />
        <CatalogSelect
          label="Đơn vị tính"
          items={materialUnits}
          value={unitId}
          onChange={setUnitId}
          required
          disabled
        />
        {materialId && !selectedMaterial?.defaultUnitId && <p className="form-error">Vật tư này chưa có đơn vị tính chuẩn. Hãy cập nhật tại Danh mục vật tư.</p>}
        <ChildBomSelect
          value={childBomVersionId}
          onChange={setChildBomVersionId}
          items={childBoms}
        />
        <FormField label="Định mức sử dụng">
          <NumericInput required value={quantity} onValueChange={setQuantity} placeholder="Ví dụ: 2,5" />
        </FormField>
        <FormField label="Mã kế toán">
          <input
            value={String(customFields.accountCode ?? "")}
            onChange={(event) => setProductionField("accountCode", event.target.value)}
          />
        </FormField>
        <div className="form-grid">
          <FormField label="Khối lượng thành phẩm">
            <NumericInput value={String(customFields.finishedWeight ?? "")} onValueChange={(value) => setProductionField("finishedWeight", value)} />
          </FormField>
          <FormField label="Khối lượng vật tư sử dụng">
            <NumericInput value={String(customFields.usedWeight ?? "")} onValueChange={(value) => setProductionField("usedWeight", value)} />
          </FormField>
        </div>
        <FormField label="Hao hụt (%)">
          <NumericInput required value={wastagePercent} onValueChange={setWastagePercent} />
        </FormField>
        <FormField label="Ghi chú">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </FormField>
        <CustomFieldInputs
          fields={fields}
          values={customFields}
          onChange={setCustomFields}
        />
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={saving || !catalogs}
          >
            <Save size={16} /> Lưu dòng
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditLineModal({
  open,
  bomId,
  line,
  onClose,
  onSaved,
}: {
  open: boolean;
  bomId: string;
  line: BomLine;
  onClose(): void;
  onSaved(): void;
}) {
  const catalogs = useCatalogs(["material", "unit"]);
  const fields = useCustomFields("bom_line");
  const [materialId, setMaterialId] = useState(line.material.id);
  const [unitId, setUnitId] = useState(line.unit.id);
  const [childBomVersionId, setChildBomVersionId] = useState(line.childBomVersionId ?? "");
  const [quantity, setQuantity] = useState(line.quantity);
  const [wastagePercent, setWastagePercent] = useState(line.wastagePercent);
  const [note, setNote] = useState(line.note);
  const [customFields, setCustomFields] = useState<Record<string, unknown>>(line.customFields ?? {});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const childBoms = useChildBomVersions(bomId, open);
  const selectedMaterial = (catalogs?.material ?? []).find((material) => material.id === materialId);
  const materialUnits = selectedMaterial?.defaultUnitId
    ? (catalogs?.unit ?? []).filter((unit) => unit.id === selectedMaterial.defaultUnitId)
    : [];
  function setProductionField(key: "accountCode" | "finishedWeight" | "usedWeight", value: string) {
    setCustomFields((current) => ({ ...current, [key]: value }));
  }
  useEffect(() => {
    if (open) {
      setMaterialId(line.material.id);
      setUnitId(line.unit.id);
      setChildBomVersionId(line.childBomVersionId ?? "");
      setQuantity(line.quantity);
      setWastagePercent(line.wastagePercent);
      setNote(line.note);
      setCustomFields(line.customFields ?? {});
      setError("");
    }
  }, [line, open]);
  function selectMaterial(id: string) {
    setMaterialId(id);
    const material = (catalogs?.material ?? []).find((item) => item.id === id);
    setUnitId(material?.defaultUnitId ?? "");
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/boms/${bomId}/lines/${line.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          materialId,
          unitId,
          quantity,
          wastagePercent,
          note,
          customFields,
          childBomVersionId: childBomVersionId || null,
        }),
      });
      onSaved();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Không thể cập nhật dòng vật tư.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} title="Cập nhật vật tư" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <CatalogSelect
          label="Vật tư"
          items={catalogs?.material ?? []}
          value={materialId}
          onChange={selectMaterial}
          required
        />
        <CatalogSelect
          label="Đơn vị tính"
          items={materialUnits}
          value={unitId}
          onChange={setUnitId}
          required
          disabled
        />
        {materialId && !selectedMaterial?.defaultUnitId && <p className="form-error">Vật tư này chưa có đơn vị tính chuẩn. Hãy cập nhật tại Danh mục vật tư.</p>}
        <ChildBomSelect
          value={childBomVersionId}
          onChange={setChildBomVersionId}
          items={childBoms}
        />
        <FormField label="Định mức sử dụng">
          <NumericInput required value={quantity} onValueChange={setQuantity} />
        </FormField>
        <FormField label="Mã kế toán">
          <input value={String(customFields.accountCode ?? "")} onChange={(event) => setProductionField("accountCode", event.target.value)} />
        </FormField>
        <div className="form-grid">
          <FormField label="Khối lượng thành phẩm">
            <NumericInput value={String(customFields.finishedWeight ?? "")} onValueChange={(value) => setProductionField("finishedWeight", value)} />
          </FormField>
          <FormField label="Khối lượng vật tư sử dụng">
            <NumericInput value={String(customFields.usedWeight ?? "")} onValueChange={(value) => setProductionField("usedWeight", value)} />
          </FormField>
        </div>
        <FormField label="Hao hụt (%)">
          <NumericInput required value={wastagePercent} onValueChange={setWastagePercent} />
        </FormField>
        <FormField label="Ghi chú">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </FormField>
        <CustomFieldInputs
          fields={fields}
          values={customFields}
          onChange={setCustomFields}
        />
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving || !catalogs}>
            <Save size={16} /> Cập nhật
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ChildBomSelect({
  value,
  onChange,
  items,
}: {
  value: string;
  onChange(value: string): void;
  items: BomListItem[];
}) {
  return (
    <FormField label="BOM con (tùy chọn)">
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Không sử dụng BOM con</option>
        {items.map((bom) => (
          <option key={bom.id} value={bom.id}>
            {bom.code} · v{bom.versionNo} — {bom.name || bom.product.name}
          </option>
        ))}
      </select>
      <small className="form-help">Khi chọn, nhu cầu sẽ được tính theo toàn bộ dòng vật tư của BOM con.</small>
    </FormField>
  );
}

function CatalogSelect({
  label,
  value,
  onChange,
  items,
  required = false,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  items: Catalog[];
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <FormField label={label}>
      <select
        required={required}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Chọn {label.toLowerCase()}</option>
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>
    </FormField>
  );
}

export { CatalogSelect, useCatalogs };
